import { Controller } from "@hotwired/stimulus"
import consumer from "channels/consumer"
import { logger } from "../logger"

// Handles real-time batch copy progress updates via ActionCable.
// Follows the same pattern as report_controller.js, reuses #reportStatuses container.
// The container is created by the inject_report_status custom Turbo Stream action
// (see turbo_custom_actions.js), so this controller only handles ActionCable subscription.
export default class extends Controller {
  static targets = ["progressBar", "message", "close"]
  static values = {
    operationId: String
  }

  connect() {
    this.isClosing = false

    if (this.hasOperationIdValue && !this.subscription) {
      this.subscription = consumer.subscriptions.create(
        {
          channel: "BatchCopyChannel",
          operation_id: this.operationIdValue
        },
        {
          connected: this.connected.bind(this),
          disconnected: this.disconnected.bind(this),
          received: this.received.bind(this)
        }
      )
    }
  }

  disconnect() {
    if (this.subscription && !this.manualCloseInProgress) {
      this.subscription.unsubscribe()
      this.subscription = null
    }
  }

  connected() {
    this.subscription.perform('check_status')
  }

  disconnected() {
    // ActionCable connection closed
  }

  received(data) {
    logger.log('BatchCopyChannel received:', data)

    if (this.isClosing) return

    if (data.status === 'progress') {
      this.updateProgress(data)
    } else if (data.status === 'done') {
      this.handleDone(data)
    } else if (data.status === 'failed') {
      this.handleFailed(data)
    } else if (data.status === 'start') {
      if (this.hasMessageTarget && data.message) {
        this.messageTarget.textContent = data.message
      }
    }
  }

  updateProgress(data) {
    if (this.hasProgressBarTarget) {
      const percent = Math.round((data.current / data.total) * 100)
      this.progressBarTarget.style.width = `${percent}%`
      this.progressBarTarget.setAttribute('aria-valuenow', percent)
    }
    if (this.hasMessageTarget && data.message) {
      this.messageTarget.textContent = data.message
    }
  }

  handleDone(data) {
    this.isClosing = true

    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = '100%'
      this.progressBarTarget.setAttribute('aria-valuenow', 100)
      this.progressBarTarget.classList.remove('progress-bar-animated', 'progress-bar-striped')
      this.progressBarTarget.classList.add('bg-success')
    }
    if (this.hasMessageTarget && data.message) {
      this.messageTarget.textContent = data.message
    }

    setTimeout(() => {
      if (this.subscription) {
        this.subscription.unsubscribe()
        this.subscription = null
      }
      this.closeStatus()
    }, 4000)
  }

  handleFailed(data) {
    this.isClosing = true

    // Hide progress bar
    if (this.hasProgressBarTarget) {
      const progressContainer = this.progressBarTarget.closest('.progress')
      if (progressContainer) progressContainer.remove()
    }

    if (this.hasMessageTarget && data.message) {
      this.messageTarget.innerHTML = `<span class="text-warning"><b>${data.message}</b></span>`
    }

    setTimeout(() => {
      if (this.subscription) {
        this.subscription.unsubscribe()
        this.subscription = null
      }
      this.closeStatus()
    }, 5000)
  }

  close(event) {
    event.preventDefault()
    if (this.subscription) {
      this.manualCloseInProgress = true
      this.subscription.unsubscribe()
      this.subscription = null
    }
    this.closeStatus()
  }

  closeStatus() {
    const container = document.getElementById('reportStatuses')

    this.element.remove()

    requestAnimationFrame(() => {
      if (container) {
        const remainingStatuses = container.querySelectorAll('.report-status').length
        if (remainingStatuses === 0) {
          container.remove()
          document.documentElement.style.setProperty('--report-status-overlay-height', '0px')
        } else if (window.updateReportStatusOverlayHeight) {
          window.updateReportStatusOverlayHeight()
        }
      }
    })
  }
}
